import { useEffect, useMemo, useRef, useState } from 'react';
import { DecimalTextInput } from '@/components/common/DecimalTextInput';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Receipt,
  Loader2,
  CheckCircle2,
  XCircle,
  Plus,
  Search,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  SlidersHorizontal,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { useDataService } from '@/lib/service-context';
import { Pagination } from '@/components/common/Pagination';
import { TabsBar } from '@/components/common/TabsBar';
import { formatTND } from '@/lib/format';
import type { DevisStatus } from '@erp/domain/pro';

const fmtIntFR = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n));

type SortKey = 'code' | 'date' | 'client' | 'pttc' | 'commission' | 'status';
type SortDir = 'asc' | 'desc';

const STATUS_LABELS: Record<DevisStatus | 'all', string> = {
  all: 'Tous',
  en_attente: 'En attente',
  valide: 'Validé',
  rejetee: 'Rejetée',
};

function StatTile({
  icon: Icon,
  label,
  value,
  trendValue,
  positive,
}: {
  icon: typeof Receipt;
  label: string;
  value: number | string;
  trendValue: number;
  positive: boolean;
}) {
  return (
    <div className="pro-card p-4 flex items-center gap-3">
      <div
        className="rounded-[10px] flex items-center justify-center shrink-0 p-3"
        style={{ background: 'var(--pro-primary)' }}
      >
        <Icon className="w-[30px] h-[30px] text-white" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-[7px]">
        <div
          className="text-[14px] leading-[1.25]"
          style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
        >
          {label}
        </div>
        <div className="flex items-end justify-between gap-2">
          <div
            className="text-[24px] font-semibold leading-[1.1] truncate"
            style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
          >
            {value}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span
              className="inline-flex items-center justify-center rounded-[12px] p-[2px]"
              style={{ background: 'var(--pro-primary-subtle)' }}
            >
              {positive ? (
                <ArrowUpRight className="w-[10px] h-[10px]" style={{ color: 'var(--pro-primary)' }} strokeWidth={2.5} />
              ) : (
                <ArrowDownRight className="w-[10px] h-[10px]" style={{ color: 'var(--pro-primary)' }} strokeWidth={2.5} />
              )}
            </span>
            <span
              className="text-[12px] leading-[1.3]"
              style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
            >
              {trendValue.toFixed(2).replace('.', ',')}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SortableTh({
  label,
  sortKey,
  current,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey | null;
  dir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const active = current === sortKey;
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th
      className="text-left py-3 px-3 text-[12px] font-medium select-none"
      style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1"
        style={{ color: active ? 'var(--pro-text-dark)' : 'var(--pro-text-grey)' }}
      >
        {label}
        <Icon className="w-3 h-3" />
      </button>
    </th>
  );
}

export function DevisListPage() {
  const { t } = useTranslation();
  const ds = useDataService();
  const navigate = useNavigate();
  const filterRef = useRef<HTMLDivElement | null>(null);
  const [tab, setTab] = useState<DevisStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [filterOpen, setFilterOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');

  useEffect(() => {
    if (!filterOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!filterRef.current?.contains(event.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [filterOpen]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const allQuery = useQuery({
    queryKey: ['pro-devis-list-all'],
    queryFn: () => ds.proDevis.list(),
  });

  const list = allQuery.data ?? [];

  const filtered = useMemo(() => {
    let l = list;
    if (tab !== 'all') l = l.filter((d) => d.status === tab);
    if (search.trim()) {
      const q = search.toLowerCase();
      l = l.filter(
        (d) =>
          d.code.toLowerCase().includes(q) ||
          d.client?.nom?.toLowerCase().includes(q) ||
          d.client?.prenom?.toLowerCase().includes(q),
      );
    }
    if (dateFrom) l = l.filter((d) => d.date >= dateFrom);
    if (dateTo) l = l.filter((d) => d.date <= dateTo + 'T23:59:59');
    const min = amountMin ? Number(amountMin) : null;
    const max = amountMax ? Number(amountMax) : null;
    if (min !== null && !isNaN(min)) l = l.filter((d) => d.pttc >= min);
    if (max !== null && !isNaN(max)) l = l.filter((d) => d.pttc <= max);
    if (sortKey) {
      const dir = sortDir === 'asc' ? 1 : -1;
      const get = (d: (typeof l)[number]) => {
        switch (sortKey) {
          case 'code':
            return d.code;
          case 'date':
            return d.date;
          case 'client':
            return [d.client?.prenom, d.client?.nom].filter(Boolean).join(' ').toLowerCase();
          case 'pttc':
            return d.pttc;
          case 'commission':
            return d.totalCommission ?? 0;
          case 'status':
            return d.status;
        }
      };
      l = [...l].sort((a, b) => {
        const va = get(a);
        const vb = get(b);
        if (va == null) return 1;
        if (vb == null) return -1;
        return va < vb ? -dir : va > vb ? dir : 0;
      });
    }
    return l;
  }, [list, tab, search, sortKey, sortDir, dateFrom, dateTo, amountMin, amountMax]);

  const activeFilterCount =
    (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (amountMin ? 1 : 0) + (amountMax ? 1 : 0);

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const counts = useMemo(() => {
    return {
      total: list.length,
      en_attente: list.filter((d) => d.status === 'en_attente').length,
      valide: list.filter((d) => d.status === 'valide').length,
      rejetee: list.filter((d) => d.status === 'rejetee').length,
    };
  }, [list]);

  const fmtDateMain = (s: string) => {
    try {
      return new Date(s).toISOString().slice(0, 10);
    } catch {
      return s;
    }
  };
  const fmtDateTime = (s: string) => {
    try {
      const d = new Date(s);
      return `${String(d.getHours()).padStart(2, '0')}h${String(d.getMinutes()).padStart(2, '0')}`;
    } catch {
      return '';
    }
  };

  const statusClass = (s: DevisStatus): string => {
    switch (s) {
      case 'en_attente':
        return 'pro-status-pending';
      case 'valide':
        return 'pro-status-validated';
      case 'rejetee':
        return 'pro-status-rejected';
      default:
        return 'pro-status-cancelled';
    }
  };

  return (
    <div className="space-y-4">
      {/* Stat cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile
          icon={Receipt}
          label={t('pro.devis.stat_total', 'Total Devis')}
          value={fmtIntFR(counts.total)}
          trendValue={1.58}
          positive
        />
        <StatTile
          icon={Loader2}
          label={t('pro.devis.stat_en_attente', 'En attente')}
          value={fmtIntFR(counts.en_attente)}
          trendValue={0.42}
          positive={false}
        />
        <StatTile
          icon={CheckCircle2}
          label={t('pro.devis.stat_valide', 'Validé')}
          value={fmtIntFR(counts.valide)}
          trendValue={2.36}
          positive
        />
        <StatTile
          icon={XCircle}
          label={t('pro.devis.stat_rejetee', 'Rejetée')}
          value={fmtIntFR(counts.rejetee)}
          trendValue={1.12}
          positive={false}
        />
      </div>

      {/* Toolbar + table card */}
      <div className="pro-card p-5 space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsBar
            tabs={(['all', 'en_attente', 'valide', 'rejetee'] as const).map((k) => ({
              key: k,
              label: t(`pro.devis.filter_${k}`, STATUS_LABELS[k]),
            }))}
            value={tab}
            onChange={(k) => {
              setTab(k);
              setPage(1);
            }}
          />
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div ref={filterRef} className="relative flex-1 sm:flex-none">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: 'var(--pro-text-grey)' }}
              />
              <input
                type="search"
                placeholder={t('pro.devis.search_placeholder', 'Rechercher un identifiant de devi...')}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10 pr-3 h-9 rounded-lg text-[13px] w-full sm:w-[260px] focus:outline-none focus:ring-2 focus:ring-orange-300"
                style={{ background: 'var(--pro-grey-subtle)', color: 'var(--pro-text-dark)' }}
              />
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setFilterOpen((o) => !o)}
                aria-label={t('pro.devis.filters', 'Filtres')}
                aria-expanded={filterOpen}
                className="w-9 h-9 rounded-lg flex items-center justify-center relative"
                style={{
                  border: '1px solid var(--pro-border)',
                  color: 'var(--pro-primary)',
                  background: filterOpen ? 'var(--pro-primary-subtle)' : 'white',
                }}
              >
                <SlidersHorizontal className="w-4 h-4" />
                {activeFilterCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[16px] h-[16px] rounded-full text-[10px] font-bold flex items-center justify-center text-white px-1"
                    style={{ background: 'var(--pro-primary)' }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </button>
              {filterOpen && (
                <div
                  className="absolute right-0 mt-2 w-[320px] rounded-xl z-20 p-4 pro-scale-in"
                  style={{
                    background: 'white',
                    border: '1px solid var(--pro-border)',
                    boxShadow: '0 12px 32px rgba(13,13,18,0.10)',
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="text-[14px] font-semibold"
                      style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                    >
                      {t('pro.devis.filters', 'Filtres')}
                    </span>
                    {activeFilterCount > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setDateFrom('');
                          setDateTo('');
                          setAmountMin('');
                          setAmountMax('');
                          setPage(1);
                        }}
                        className="text-[12px] font-medium"
                        style={{ color: 'var(--pro-primary)' }}
                      >
                        {t('pro.devis.reset_filters', 'Réinitialiser')}
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label
                        className="text-[12px] font-medium mb-1.5 block"
                        style={{ color: 'var(--pro-text-grey)' }}
                      >
                        {t('pro.devis.filter_date_range', 'Plage de dates')}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="date"
                          value={dateFrom}
                          onChange={(e) => {
                            setDateFrom(e.target.value);
                            setPage(1);
                          }}
                          className="h-9 px-2 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-orange-300"
                          style={{
                            border: '1px solid var(--pro-border)',
                            color: 'var(--pro-text-dark)',
                          }}
                        />
                        <input
                          type="date"
                          value={dateTo}
                          onChange={(e) => {
                            setDateTo(e.target.value);
                            setPage(1);
                          }}
                          className="h-9 px-2 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-orange-300"
                          style={{
                            border: '1px solid var(--pro-border)',
                            color: 'var(--pro-text-dark)',
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        className="text-[12px] font-medium mb-1.5 block"
                        style={{ color: 'var(--pro-text-grey)' }}
                      >
                        {t('pro.devis.filter_amount_range', 'Montant TTC (DT)')}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <DecimalTextInput
                          maxDecimals={3}
                          placeholder={t('pro.devis.min', 'Min')}
                          value={amountMin}
                          onTextChange={(s) => {
                            setAmountMin(s);
                            setPage(1);
                          }}
                          className="h-9 px-2 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-orange-300"
                          style={{
                            border: '1px solid var(--pro-border)',
                            color: 'var(--pro-text-dark)',
                          }}
                        />
                        <DecimalTextInput
                          maxDecimals={3}
                          placeholder={t('pro.devis.max', 'Max')}
                          value={amountMax}
                          onTextChange={(s) => {
                            setAmountMax(s);
                            setPage(1);
                          }}
                          className="h-9 px-2 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-orange-300"
                          style={{
                            border: '1px solid var(--pro-border)',
                            color: 'var(--pro-text-dark)',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Link
              to="/checkout?mode=devis"
              aria-label={t('pro.devis.new', 'Nouveau')}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12px] font-semibold text-white shrink-0"
              style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden md:inline">{t('pro.devis.new', 'Nouveau')}</span>
            </Link>
          </div>
        </div>

        <div key={tab} className="pro-fade-in space-y-5">
        {allQuery.isLoading ? (
          <div className="p-12 text-center" style={{ color: 'var(--pro-text-grey)' }}>
            …
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <FileText
              className="w-10 h-10 mx-auto mb-2"
              style={{ color: 'var(--pro-text-grey-light)' }}
            />
            <p style={{ color: 'var(--pro-text-grey)' }}>
              {t('pro.devis.empty', 'Aucun devis trouvé')}
            </p>
          </div>
        ) : (
          <>
          {/* Mobile: stacked cards (the 7-column table can't fit a phone) */}
          <div className="md:hidden space-y-3">
            {paged.map((d) => (
              <Link
                key={d.code}
                to={`/devis/${encodeURIComponent(d.code)}`}
                className="block rounded-xl p-4 space-y-3 active:bg-orange-50/40"
                style={{ border: '1px solid var(--pro-line)' }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[12px] font-medium" style={{ color: 'var(--pro-text-grey)' }}>
                    {d.code}
                  </span>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium shrink-0 ${statusClass(d.status)}`}
                  >
                    {STATUS_LABELS[d.status]}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="text-[14px] font-semibold truncate"
                    style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                  >
                    {[d.client?.prenom, d.client?.nom].filter(Boolean).join(' ') || '—'}
                  </span>
                  <span className="text-[12px] shrink-0" style={{ color: 'var(--pro-text-grey)' }}>
                    {fmtDateMain(d.date)} · {fmtDateTime(d.date)}
                  </span>
                </div>
                <div
                  className="flex items-center justify-between gap-2 pt-3"
                  style={{ borderTop: '1px dashed var(--pro-line)' }}
                >
                  <div>
                    <div className="text-[11px]" style={{ color: 'var(--pro-text-grey)' }}>Total TTC</div>
                    <div className="text-[14px] font-semibold" style={{ color: 'var(--pro-text-dark)' }}>
                      {formatTND(d.pttc)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px]" style={{ color: 'var(--pro-text-grey)' }}>Commission</div>
                    <div className="text-[14px] font-semibold" style={{ color: 'var(--pro-primary)' }}>
                      {formatTND(d.totalCommission ?? 0)}
                    </div>
                  </div>
                </div>
                <span
                  className="flex w-full items-center justify-center px-3 py-2 rounded-lg text-[12px] font-semibold"
                  style={{
                    background: 'var(--pro-primary-subtle)',
                    color: 'var(--pro-text-dark)',
                    fontFamily: 'Figtree',
                  }}
                >
                  {d.status === 'en_attente'
                    ? t('pro.devis.action_passer', 'Passer une commande')
                    : d.status === 'valide'
                      ? t('pro.devis.action_view_order', 'Voir la commande')
                      : t('pro.devis.action_view', 'Voir')}
                </span>
              </Link>
            ))}
          </div>
          {/* Desktop: full table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--pro-line)' }}>
                  <SortableTh label="Ref Devis" sortKey="code" current={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Date" sortKey="date" current={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Client" sortKey="client" current={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Total TTC" sortKey="pttc" current={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Commission" sortKey="commission" current={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Statut" sortKey="status" current={sortKey} dir={sortDir} onSort={handleSort} />
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {paged.map((d) => (
                  <tr
                    key={d.code}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/devis/${encodeURIComponent(d.code)}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(`/devis/${encodeURIComponent(d.code)}`);
                      }
                    }}
                    style={{ borderBottom: '1px solid var(--pro-line)' }}
                    className="cursor-pointer hover:bg-orange-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
                  >
                    <td className="py-3 px-3" style={{ color: 'var(--pro-text-grey)' }}>
                      {d.code}
                    </td>
                    <td className="py-3 px-3">
                      <div style={{ color: 'var(--pro-text-dark)' }}>{fmtDateMain(d.date)}</div>
                      <div className="text-[11px]" style={{ color: 'var(--pro-text-grey)' }}>
                        {fmtDateTime(d.date)}
                      </div>
                    </td>
                    <td className="py-3 px-3" style={{ color: 'var(--pro-text-dark)' }}>
                      {[d.client?.prenom, d.client?.nom].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="py-3 px-3" style={{ color: 'var(--pro-text-dark)' }}>
                      {formatTND(d.pttc)}
                    </td>
                    <td className="py-3 px-3 font-semibold" style={{ color: 'var(--pro-primary)' }}>
                      {formatTND(d.totalCommission ?? 0)}
                    </td>
                    <td className="py-3 px-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${statusClass(d.status)}`}
                      >
                        {STATUS_LABELS[d.status]}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <Link
                        to={`/devis/${encodeURIComponent(d.code)}`}
                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-[12px] font-semibold"
                        style={{
                          background: 'var(--pro-primary-subtle)',
                          color: 'var(--pro-text-dark)',
                          fontFamily: 'Figtree',
                        }}
                      >
                        {d.status === 'en_attente'
                          ? t('pro.devis.action_passer', 'Passer une commande')
                          : d.status === 'valide'
                            ? t('pro.devis.action_view_order', 'Voir la commande')
                            : t('pro.devis.action_view', 'Voir')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {filtered.length > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        )}
        </div>
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      className="text-left py-3 px-3 text-[12px] font-medium"
      style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
    >
      {children}
    </th>
  );
}
